import { useEffect, useMemo, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Truck } from 'lucide-react';
import { azureMapsAuthHeaders, getAzureMapsToken } from '@my/directory-api';
import { useMapsConfig } from '@my/directory-api/hooks';

/** Ireland-centred fallback view ([lng, lat] — Azure uses GeoJSON order). */
const COORD_DEFAULT: atlas.data.Position = [-6.26, 53.35];

export type ManifestMapPoint = {
  /** Stable id (used to key marker rebuilds). */
  id: string;
  lat: number;
  lng: number;
  /** Pin role → colour. depot = start, queue = unassigned, load = on selected load. */
  kind: 'depot' | 'queue' | 'load';
  /** Optional label drawn inside the pin (e.g. the load sequence number). */
  label?: string;
  /** Hover-popup heading (e.g. "Order #2606-000004"). */
  title?: string;
  /** Hover-popup detail lines under the heading. */
  details?: string[];
};

/** Pin colour per role. */
function colorFor(kind: ManifestMapPoint['kind']): string {
  if (kind === 'depot') return '#10b981'; // emerald — start
  if (kind === 'load') return '#a855f7'; // purple — on selected load
  return '#3b82f6'; // blue — queue
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => HTML_ESCAPES[c] ?? c);
}

/** Small info card shown on pin hover. */
function popupHtml(p: ManifestMapPoint): string {
  const head = escapeHtml(p.title ?? '');
  const lines = (p.details ?? [])
    .filter(Boolean)
    .map((d) => `<div style="color:#475569">${escapeHtml(d)}</div>`)
    .join('');
  return (
    `<div style="padding:6px 9px;font-size:12px;line-height:1.35;max-width:240px;font-family:inherit;">` +
    (head ? `<div style="font-weight:600;color:#0f172a">${head}</div>` : '') +
    lines +
    `</div>`
  );
}

type RoutePoint = { lat: number; lng: number };

/** Optimized truck route to draw: depot → (waypoints, order optimized) → furthest. */
export type ManifestRoute = {
  origin: RoutePoint;
  destination: RoutePoint;
  waypoints: RoutePoint[];
};

/**
 * Heavy-truck profile applied to every route request, so Azure honours
 * truck-specific restrictions (low bridges, weight/width limits, lorry bans)
 * rather than returning a car route. Values are typical for a 6-axle artic
 * under IE/UK limits; once the load's truck/trailer resource carries real
 * dimensions, source these from it instead of the defaults.
 */
const TRUCK_PROFILE = {
  vehicleHeight: 4.0, // metres
  vehicleWidth: 2.55, // metres
  vehicleLength: 16.5, // metres
  vehicleWeight: 44000, // kg, gross
  vehicleAxleWeight: 11500, // kg, max single axle
  vehicleMaxSpeed: 90, // km/h
} as const;

/** Minimal shape of the Azure Route Directions response we read. */
interface RouteDirectionsResponse {
  routes?: Array<{
    summary?: { lengthInMeters?: number; travelTimeInSeconds?: number };
    legs?: Array<{ points?: Array<{ latitude?: number; longitude?: number }> }>;
  }>;
}

export interface TruckRouteResult {
  coords: atlas.data.Position[];
  meters: number;
  seconds: number;
}

/**
 * Call the Azure Maps Route Directions API in **truck** mode (full vehicle
 * profile applied) and return the road polyline plus distance/drive-time. Azure
 * optimizes the waypoint order (`computeBestOrder`) between the fixed depot
 * origin and the furthest stop. Returns null on any failure so the caller just
 * skips drawing the route.
 */
async function fetchTruckRoute(
  clientId: string,
  route: ManifestRoute,
): Promise<TruckRouteResult | null> {
  const pts = [route.origin, ...route.waypoints, route.destination];
  const url = new URL('https://atlas.microsoft.com/route/directions/json');
  url.searchParams.set('api-version', '1.0');
  url.searchParams.set('query', pts.map((p) => `${p.lat},${p.lng}`).join(':'));
  url.searchParams.set('travelMode', 'truck');
  url.searchParams.set('vehicleCommercial', 'true');
  url.searchParams.set('vehicleHeight', String(TRUCK_PROFILE.vehicleHeight));
  url.searchParams.set('vehicleWidth', String(TRUCK_PROFILE.vehicleWidth));
  url.searchParams.set('vehicleLength', String(TRUCK_PROFILE.vehicleLength));
  url.searchParams.set('vehicleWeight', String(TRUCK_PROFILE.vehicleWeight));
  url.searchParams.set('vehicleAxleWeight', String(TRUCK_PROFILE.vehicleAxleWeight));
  url.searchParams.set('vehicleMaxSpeed', String(TRUCK_PROFILE.vehicleMaxSpeed));
  if (route.waypoints.length > 0) url.searchParams.set('computeBestOrder', 'true');

  try {
    // A short-lived token from Directory — the subscription key never
    // reaches the browser.
    const res = await fetch(url.toString(), { headers: await azureMapsAuthHeaders(clientId) });
    if (!res.ok) return null;
    const data = (await res.json()) as RouteDirectionsResponse;
    const top = data.routes?.[0];
    const coords: atlas.data.Position[] = [];
    for (const leg of top?.legs ?? []) {
      for (const pt of leg.points ?? []) {
        if (typeof pt.longitude === 'number' && typeof pt.latitude === 'number') {
          coords.push([pt.longitude, pt.latitude]);
        }
      }
    }
    if (!coords.length) return null;
    return {
      coords,
      meters: top?.summary?.lengthInMeters ?? 0,
      seconds: top?.summary?.travelTimeInSeconds ?? 0,
    };
  } catch {
    return null;
  }
}

/** "142 km" / "8.4 km". */
function formatKm(meters: number): string {
  const km = meters / 1000;
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
}

/** "2h 10m" / "45 min". */
function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Multi-pin manifest map on **Azure Maps** — depot start pin plus every supplied
 * point, auto-fitting bounds. Azure (rather than Google, which still powers
 * address geocoding) is used here so the same map can later drive truck route
 * planning/optimization via the Azure Maps Route service (vehicle type = Truck).
 *
 * Self-contained: creates its own `atlas.Map`, signed in with short-lived
 * tokens from Directory (`GET maps/azure-token`) and the Maps account's client
 * id from `GET maps/config` — no Azure key in the browser.
 */
export function ManifestMap({
  points,
  route,
}: {
  points: ManifestMapPoint[];
  route?: ManifestRoute | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const markersRef = useRef<atlas.HtmlMarker[]>([]);
  const popupRef = useRef<atlas.Popup | null>(null);
  const routeSourceRef = useRef<atlas.source.DataSource | null>(null);
  const [ready, setReady] = useState(false);
  const [summary, setSummary] = useState<{ meters: number; seconds: number } | null>(null);

  const mapsConfig = useMapsConfig();
  const clientId = mapsConfig.data?.azureMapsClientId ?? '';

  // Stable signature so markers only rebuild when the plotted points change.
  const pointsKey = useMemo(
    () =>
      points
        .map((p) => `${p.kind}:${p.id}:${p.lat.toFixed(5)},${p.lng.toFixed(5)}:${p.label ?? ''}`)
        .join('|'),
    [points],
  );

  // Stable signature so the route only refetches when its endpoints change.
  const routeKey = useMemo(() => {
    if (!route) return '';
    const f = (p: RoutePoint) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
    return [route.origin, ...route.waypoints, route.destination].map(f).join('|');
  }, [route]);

  // Create the map once (per client id). Tear down on unmount.
  useEffect(() => {
    if (!containerRef.current || !clientId) return;
    const map = new atlas.Map(containerRef.current, {
      center: COORD_DEFAULT,
      zoom: 6,
      style: 'road',
      authOptions: {
        // Token auth: the map asks for a fresh token whenever it needs one.
        authType: atlas.AuthenticationType.anonymous,
        clientId,
        getToken: (resolve, reject) => {
          getAzureMapsToken().then(resolve, reject);
        },
      },
    });
    mapRef.current = map;
    // One reusable hover popup, shown/hidden as the pointer enters/leaves a pin.
    popupRef.current = new atlas.Popup({ closeButton: false, pixelOffset: [0, -34] });
    map.events.add('ready', () => {
      // A data source + line layer for the optimized truck route. Drawn beneath
      // the HTML marker pins (markers are DOM overlays, always on top).
      const source = new atlas.source.DataSource();
      map.sources.add(source);
      routeSourceRef.current = source;
      map.layers.add(
        new atlas.layer.LineLayer(source, undefined, {
          strokeColor: '#7c3aed',
          strokeWidth: 5,
          strokeOpacity: 0.85,
          lineJoin: 'round',
          lineCap: 'round',
        }),
      );
      setReady(true);
    });

    return () => {
      setReady(false);
      markersRef.current = [];
      routeSourceRef.current = null;
      popupRef.current?.close();
      popupRef.current = null;
      map.dispose();
      mapRef.current = null;
    };
  }, [clientId]);

  // Fetch + draw the optimized truck route whenever its endpoints change.
  useEffect(() => {
    const source = routeSourceRef.current;
    if (!source || !ready) return;
    source.clear();
    setSummary(null);
    if (!route || !clientId) return;
    let cancelled = false;
    void fetchTruckRoute(clientId, route).then((result) => {
      if (cancelled || !result) return;
      source.add(new atlas.data.LineString(result.coords));
      setSummary({ meters: result.meters, seconds: result.seconds });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, routeKey, clientId]);

  // (Re)draw markers + fit camera when the points change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const popup = popupRef.current;

    popup?.close();
    markersRef.current.forEach((m) => map.markers.remove(m));
    markersRef.current = [];

    if (points.length === 0) {
      map.setCamera({ center: COORD_DEFAULT, zoom: 6 });
      return;
    }

    // HTML markers stack by insertion order (last = on top). Draw queue first,
    // then the load's stops, then the depot — so the green depot pin is always
    // visible even when a collection sits at the very same address.
    const RENDER_ORDER: Record<ManifestMapPoint['kind'], number> = {
      queue: 0,
      load: 1,
      depot: 2,
    };
    const ordered = [...points].sort((a, b) => RENDER_ORDER[a.kind] - RENDER_ORDER[b.kind]);

    const positions: atlas.data.Position[] = [];
    for (const p of ordered) {
      const position: atlas.data.Position = [p.lng, p.lat];
      const marker = new atlas.HtmlMarker({
        position,
        color: colorFor(p.kind),
        text: p.label ?? '',
      });
      map.markers.add(marker);
      markersRef.current.push(marker);
      positions.push(position);

      if (popup && (p.title || p.details?.length)) {
        map.events.add('mouseover', marker, () => {
          popup.setOptions({ content: popupHtml(p), position });
          popup.open(map);
        });
        map.events.add('mouseout', marker, () => popup.close());
      }
    }

    const only = positions.length === 1 ? positions[0] : undefined;
    if (only) {
      map.setCamera({ center: only, zoom: 11 });
    } else {
      map.setCamera({
        bounds: atlas.data.BoundingBox.fromPositions(positions),
        padding: 56,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, pointsKey]);

  if (!clientId) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 text-center text-[11px] italic text-slate-400">
        {mapsConfig.isPending
          ? 'Loading map…'
          : 'Map unavailable — Azure Maps is not configured for this environment (AzureMaps:ClientId).'}
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      {summary && (
        <div className="pointer-events-none absolute left-2 top-2 flex items-center gap-1.5 rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
          <Truck className="h-3.5 w-3.5 text-purple-600" />
          <span className="tabular-nums">{formatKm(summary.meters)}</span>
          <span className="text-slate-300">·</span>
          <span className="tabular-nums">{formatDuration(summary.seconds)}</span>
        </div>
      )}
    </div>
  );
}
